<template>
    <ChatAgentToolFrame class="@container mx-2 mt-4 mb-5 w-full place-self-start gap-0 rounded-2xl p-0" :data-testid="testId">
        <div class="relative flex flex-col gap-4 px-6 pt-6 pb-5">
            <BaseButton
                theme="ghost"
                size="sm"
                icon-only
                type="button"
                :aria-label="t('chat.proactiveOffer.dismissAriaLabel', { kind: offerKindLabel })"
                class="absolute top-3 right-3"
                :disabled="busy"
                @click="emit('dismiss')">
                <i class="bi bi-x-lg text-sm/none" aria-hidden="true"></i>
            </BaseButton>
            <BaseChip size="2xs" class="offer-chip w-fit gap-2 px-3">
                <template #leading><span class="size-2 shrink-0 rounded-full bg-current" aria-hidden="true"></span></template>
                {{ offerLabel === "feature" ? t("chat.proactiveOffer.chipEnhancement") : t("chat.proactiveOffer.chipMoment") }}
            </BaseChip>
            <div class="flex flex-col gap-1 pr-4">
                <h3 class="nadia-chat-bold text-ink">{{ header }}</h3>
                <p class="body-small text-ink-muted">{{ subtext }}</p>
            </div>
        </div>
        <div class="rounded-b-2xl border-t border-border bg-surface-raised py-3 pl-6 pr-3">
            <div class="flex flex-col gap-1 @lg:flex-row @lg:items-center @lg:justify-between">
                <div class="flex items-center gap-2">
                    <span class="scaffold-details-small text-ink-muted">{{ t("chat.proactiveOffer.helpfulPrompt") }}</span>
                    <div class="flex items-center gap-1">
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :aria-label="t('chat.proactiveOffer.helpfulAriaLabel', { kind: offerKindLabel })"
                            :aria-pressed="feedback === 'up'"
                            :disabled="busy"
                            class="feedback-button"
                            @click="emit('rate', 'up')">
                            <ThumbsUp aria-hidden="true" />
                        </BaseButton>
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :aria-label="t('chat.proactiveOffer.notHelpfulAriaLabel', { kind: offerKindLabel })"
                            :aria-pressed="feedback === 'down'"
                            :disabled="busy"
                            class="feedback-button"
                            @click="emit('rate', 'down')">
                            <ThumbsDown aria-hidden="true" />
                        </BaseButton>
                    </div>
                </div>
                <div class="flex flex-col gap-2 @sm:flex-row @sm:items-center">
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        :aria-label="t('chat.proactiveOffer.saveForLaterAriaLabel', { kind: offerKindLabel })"
                        :aria-pressed="savedForLater"
                        :disabled="busy"
                        @click="emit('toggleSaved')">
                        <span class="flex items-center justify-center gap-2">
                            <i v-if="savedForLater" class="bi bi-check-lg" aria-hidden="true"></i>
                            <span>{{ savedForLater ? t("chat.proactiveOffer.saved") : t("chat.proactiveOffer.saveForLater") }}</span>
                        </span>
                    </BaseButton>
                    <BaseButton theme="primary" size="sm" data-testid="proactive-offer-cta" :disabled="busy" class="self-stretch @sm:self-auto" @click="emit('cta')">
                        <span class="flex items-center justify-center gap-2">
                            <LoadingSpinner v-if="loading" class="h-4 w-4" />
                            {{ ctaLabel }}
                            <i v-if="offerLabel === 'feature'" class="bi bi-box-arrow-up-right shrink-0" aria-hidden="true"></i>
                            <span v-if="offerLabel === 'feature'" class="sr-only">{{ t("chat.proactiveOffer.opensInNewTab") }}</span>
                        </span>
                    </BaseButton>
                </div>
            </div>
            <span role="status" class="sr-only">{{
                savedForLater ? t("chat.proactiveOffer.savedStatus", { kind: offerKindLabel }) : t("chat.proactiveOffer.notSavedStatus", { kind: offerKindLabel })
            }}</span>
            <BaseNotification v-if="errorMessage" theme="error" :text="errorMessage" class="mt-2" hide-dismiss />
        </div>
    </ChatAgentToolFrame>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import ThumbsDown from "~vue/icons/ThumbsDown.vue";
import ThumbsUp from "~vue/icons/ThumbsUp.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

const { t } = useI18n();
const props = defineProps<{
    header: string;
    subtext: string;
    ctaLabel: string;
    offerLabel: "coaching" | "feature";
    testId: string;
    feedback: "up" | "down" | null;
    savedForLater: boolean;
    busy: boolean;
    loading: boolean;
    errorMessage: string;
}>();

const offerKindLabel = computed(() => (props.offerLabel === "feature" ? t("chat.proactiveOffer.kindFeature") : t("chat.proactiveOffer.kindCoaching")));

const emit = defineEmits<{ dismiss: []; cta: []; rate: ["up" | "down"]; toggleSaved: [] }>();
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .offer-chip {
        --base-chip-bg: var(--color-status-success-soft);
        --base-chip-text: var(--color-chip-neutral-text);
    }

    /* Resting (not pressed) = default tertiary look: no fill, no visible outline. The transparent
       border just reserves space so the pressed state's border doesn't shift the icon. */
    .feedback-button {
        @apply border border-transparent;
    }

    .feedback-button[aria-pressed="false"]:hover {
        @apply border-border-strong bg-button-ghost-hover;
    }

    /* Neutral selected state via the ghost button's own active token, not the magenta accent. */
    .feedback-button[aria-pressed="true"] {
        @apply border-border-strong bg-button-ghost-active text-ink;
    }
}
</style>
